/* =====================================================================
 * AuthProviderRegistry + useAuthProviders.
 *
 * React binding that exposes the configured auth providers to any
 * component in the tree. The registry is mounted once near the app
 * root (after `TenantProvider`), receives either a pre-built
 * provider list or a list of configs, and memoises the result.
 *
 * Why expose providers as an array rather than named props
 *   B2B tenants stack multiple login affordances ("Google" + "Okta
 *   SAML" + "Email/password"); the auth screen simply iterates over
 *   the array and renders a button per entry. B2C uses a single-
 *   element array so the rendering code path is identical.
 *
 * Naming nuance
 *   React's own `<Provider>` component pattern clashes with
 *   "auth provider", so we call the React context `AuthProvider-
 *   Registry` and reserve `AuthProvider` (singular) for the runtime
 *   value returned by each factory.
 * ===================================================================== */

import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import { createAuthProviders } from './registry';
import type { AuthProvider, AuthProviderConfig, AuthProviderKind } from './types';

const AuthProviderRegistryContext = createContext<readonly AuthProvider[] | null>(
  null,
);

export interface AuthProviderRegistryProps {
  /**
   * Fully-built providers. Use this when the app needs to wire
   * factory callbacks that depend on app-local state (e.g. the
   * Google provider's `buildLoginUrl`).
   */
  providers?: readonly AuthProvider[];
  /**
   * Raw configs to materialise. Shortcut for tenants whose providers
   * don't need app-level DI. Ignored when `providers` is supplied.
   */
  configs?: readonly AuthProviderConfig[];
  children: ReactNode;
}

/**
 * React provider component. Memoises the resolved list so rendering
 * an auth screen doesn't rebuild every provider on each render.
 */
export const AuthProviderRegistry: React.FC<AuthProviderRegistryProps> = ({
  providers,
  configs,
  children,
}) => {
  const resolved = useMemo<readonly AuthProvider[]>(() => {
    if (providers && providers.length > 0) {
      return providers;
    }
    if (configs && configs.length > 0) {
      return createAuthProviders(configs);
    }
    return [];
  }, [providers, configs]);

  return (
    <AuthProviderRegistryContext.Provider value={resolved}>
      {children}
    </AuthProviderRegistryContext.Provider>
  );
};

/** Read the current provider list. Returns an empty array when the
 *  registry hasn't been mounted, so callers always receive an
 *  iterable. */
export function useAuthProviders(): readonly AuthProvider[] {
  return useContext(AuthProviderRegistryContext) ?? [];
}

/** Convenience accessor that returns the first provider matching a kind.
 *  Returns `null` when absent. */
export function useAuthProvider(kind: AuthProviderKind): AuthProvider | null {
  const providers = useAuthProviders();
  for (const provider of providers) {
    if (provider.kind === kind) return provider;
  }
  return null;
}

/** Convenience accessor by explicit provider id. */
export function useAuthProviderById(id: string): AuthProvider | null {
  const providers = useAuthProviders();
  for (const provider of providers) {
    if (provider.id === id) return provider;
  }
  return null;
}
